import { useEffect, useRef, useState } from "react"
import { ArrowLeft, ShieldCheck } from "lucide-react"

import { SixDigitOtpInput } from "@/components/shared/SixDigitOtpInput"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export function TwoFactorChallengeForm({ onVerify, onVerifyBackup, onBack }) {
  const [mode, setMode] = useState("totp")
  const [code, setCode] = useState("")
  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const codeInputRef = useRef(null)

  const title = mode === "totp" ? "Verification code" : "Backup code"
  const submitLabel = mode === "totp" ? "Verify code" : "Verify backup code"
  const canSubmit = mode === "totp" ? code.length === 6 : code.trim().length > 0

  useEffect(() => {
    if (mode === "totp") {
      codeInputRef.current?.focus()
    }
  }, [mode])

  async function handleSubmit(event) {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      if (mode === "totp") {
        await onVerify({ code })
      } else {
        await onVerifyBackup({ code })
      }
    } catch (e) {
      setError(e.message)
      setCode("")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate autoComplete="off">
      <div className="rounded-2xl border border-border bg-card p-5 text-center shadow-sm">
        <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
          <ShieldCheck className="size-5 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Two-factor authentication</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {mode === "totp"
            ? "Enter the 6-digit code from your authenticator app."
            : "Enter one unused backup code."}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="two_factor_code" className="text-sm font-semibold text-foreground">
          {title}
        </Label>
        {mode === "totp" ? (
          <SixDigitOtpInput
            id="two_factor_code"
            ariaLabel="Verification code"
            ref={codeInputRef}
            value={code}
            onChange={setCode}
          />
        ) : (
          <Input
            id="two_factor_code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="one-time-code"
            className="h-12 rounded-xl text-center font-mono text-lg tracking-widest"
            required
          />
        )}
      </div>

      {error ? (
        <p role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={isSubmitting || !canSubmit}
        className="h-12 w-full rounded-xl text-sm font-bold"
      >
        {isSubmitting ? "Verifying..." : submitLabel}
      </Button>

      <div className="flex flex-col items-center gap-3 text-sm">
        <button
          type="button"
          onClick={() => {
            setMode(mode === "totp" ? "backup" : "totp")
            setCode("")
            setError(null)
          }}
          className="font-medium text-primary hover:underline"
        >
          {mode === "totp" ? "Use a backup code instead" : "Use authenticator app instead"}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to sign in
        </button>
      </div>
    </form>
  )
}
